/* eslint-disable no-console */
import { io, Socket } from 'socket.io-client';
import { SocketEvents } from '../api/socket.types';
import {
  getUniqueDeviceIdForTab, saveWsntToken, getWsntToken, 
} from 'lib/api';
import controller from 'modules/auth/store/controller';

class SocketService {
  private socket: Socket | null = null;
  private eventCallbacks: Map<string, Function[]> = new Map();
  private currentUrl: string = process.env.NOTIFICATION_SERVER;
  private isReconnectingDueToAuthError = false;

  constructor() {
    // Разрыв сокета при любом logout (clearCredentials диспатчит 'auth:logout').
    // Покрывает три точки сразу: 430-interceptor, клиентский logout, TokenExpiredModal.
    if (typeof window !== 'undefined') {
      window.addEventListener('auth:logout', () => {
        this.isReconnectingDueToAuthError = false;
        this.disconnect();
      });
    }
  }

  // Установка URL
  setUrl(url: string): void {
    this.currentUrl = url;
  }

  // Получение текущего URL
  getUrl(): string {
    return this.currentUrl;
  }

  // Получение нового wsnt-токена
  private async fetchNewWsntToken(deviceId: string): Promise<string> {
    const response = await controller.getNotificationsWsnt(deviceId);
    const token = response?.data?.wsnt;
    if (!token) {
      throw new Error('Failed to retrieve wsnt token from server');
    }
    return token;
  }

  // Подключение к WebSocket
  connect(url?: string): Socket | null {
    if (this.socket?.connected) {
      console.warn('Already connected. Skipping new connection.');
      return this.socket;
    }

    if (url) {
      this.currentUrl = url;
    }

    // ✅ Получаем уникальный deviceId для этой вкладки
    const uniqueDeviceId = getUniqueDeviceIdForTab();
    if (!uniqueDeviceId) {
      console.error('Device ID is missing. Cannot connect to WebSocket.');
      this.emitEvent('connection:error', new Error('Missing device ID'));
      return null;
    }

    // ✅ Сначала пробуем использовать существующий токен из sessionStorage
    let wsntToken = getWsntToken();

    if (!wsntToken) {
    // Если нет — запрашиваем новый
      this.fetchNewWsntToken(uniqueDeviceId)
        .then(newToken => {
          wsntToken = newToken;
          saveWsntToken(wsntToken); // ✅ Сохраняем в sessionStorage

          this.socket = io(this.currentUrl, {
            transports: ['websocket'],
            reconnection: false,
            autoConnect: true,
            auth: { token: wsntToken },
          });

          this.setupBaseListeners();
        })
        .catch(error => {
          console.error('Failed to initialize WebSocket connection:', error);
          this.emitEvent('connection:error', error);
        });
    } else {
    // ✅ Используем существующий токен
      this.socket = io(this.currentUrl, {
        transports: ['websocket'],
        reconnection: false,
        autoConnect: true,
        auth: { token: wsntToken },
      });

      this.setupBaseListeners();
    }

    return this.socket;
  }

  private setupBaseListeners(): void {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✅ Connected to WebSocket server');
      this.isReconnectingDueToAuthError = false;
      this.emitEvent('connection:status', true);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Disconnected:', reason);
      this.emitEvent('connection:status', false);
    });

    this.socket.on('connect_error', (err) => {
      console.error('❌ Connection error:', err.message || err);

      if (this.isAuthError(err) && !this.isReconnectingDueToAuthError) {
        console.log('🔐 Detected auth error — refreshing wsnt token and reconnecting...');
        this.isReconnectingDueToAuthError = true;
        this.refreshWsntAndReconnect().catch(e => {
          console.error('Failed to refresh token after auth error:', e);
          this.isReconnectingDueToAuthError = false;
        });
      } else {
        this.emitEvent('connection:error', err);
      }
    });

    // Слушатели бизнес-событий
    this.socket.on('hello', (message: unknown, callback?: (ack: string) => void) => {
      console.log('👋 Received hello:', message);
      this.emitEvent('hello', { message, callback });
      callback?.('acknowledged');
    });

    this.socket.on('push', (message: unknown, callback?: (ack: string) => void) => {
      if (typeof message !== 'object' || message === null) {
        console.error('Invalid push message format:', message);
        return;
      }
      console.log('📬 Received push:', message);
      this.emitEvent('push', { message, callback });
      callback?.('acknowledged');
    });

    this.socket.on(
      'event',
      (data: Record<string, unknown>, callback: (ack: string) => void) => {
        console.log(
          this.socket?.recovered
            ? '🔄 Received missed event (after recovery):'
            : '📡 Received live event:',
          data
        );
        this.emitEvent('event', { data, callback });
        callback?.('acknowledged');
      }
    );

    this.socket.on('message:received', (message: unknown) => {
      this.emitEvent('message:received', message);
    });
  }

  // Определяет, связана ли ошибка с аутентификацией
  private isAuthError(err: any): boolean {
    const msg = String(err?.message || '').toLowerCase();
    const authKeywords = [
      'auth',
      'token',
      'unauthorized',
      'expired',
      'invalid',
      '401',
    ];
    return authKeywords.some(keyword => msg.includes(keyword));
  }

  // Обновляет токен и переподключается
  private async refreshWsntAndReconnect(): Promise<void> {
    const uniqueDeviceId = getUniqueDeviceIdForTab();
    if (!uniqueDeviceId) {
      throw new Error('Device ID missing during token refresh');
    }

    const newWsntToken = await this.fetchNewWsntToken(uniqueDeviceId);
    saveWsntToken(newWsntToken); // ✅ Только в sessionStorage

    console.log('🆕 New wsnt token saved. Reconnecting...');
    this.reconnect();
  }

  // Переподключение
  reconnect(url?: string): void {
    this.disconnect();
    if (url) this.currentUrl = url;
    console.log(`🔄 Reconnecting to ${ this.currentUrl }...`);
    this.connect();
  }

  // Отключение
  disconnect(): void {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      console.log('🛑 Socket disconnected and cleaned up');
    }
  }

  // Подписка на события
  on<T extends keyof SocketEvents>(event: T, callback: SocketEvents[T]): void {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
    }
    this.eventCallbacks.get(event)?.push(callback);
  }

  // Отписка
  off<T extends keyof SocketEvents>(event: T, callback: SocketEvents[T]): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  // Отправка события на сервер
   
  // ✅ ПРАВИЛЬНАЯ ВЕРСИЯ
  emit<T extends keyof SocketEvents>(event: T, ...args: Parameters<SocketEvents[T]>): void {
    if (this.socket) {
      this.socket.emit(event, ...args);
    }
  }

  // Внутренняя отправка локальных событий
  private emitEvent<T extends keyof SocketEvents>(event: T, data: any): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach(cb => cb(data));
    }
  }

  // Проверка подключения
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }
}

export const socketService = new SocketService();